import { Button, SxProps, Theme } from "@mui/material";
import { Action } from "../util/func";
import { ColorHexCodes } from "../util/Color";
import { ReactNode } from "react";

export type ButtonStyle = "primary" | "secondary" | "negative" | "text" | "bordered";
export type ButtonSize = "medium" | "small" | "large";

type StyledButtonProps = {
  ref?: React.Ref<HTMLButtonElement>;
  children: ReactNode;
  buttonStyle: ButtonStyle;
  sx?: SxProps<Theme>;
  onClick: Action<void>;
  fullWidth?: boolean;
  disabled?: boolean;
  size?: ButtonSize;
  startIcon?: ReactNode;
};

export default function StyledButton({ ref, children, buttonStyle, sx, onClick, fullWidth, disabled, size, startIcon }: StyledButtonProps) {
  sx = { minWidth: 0, ...sx };

  if (disabled) {
    switch (buttonStyle) {
      case "primary":
      case "secondary":
      case "negative":
        sx = {
          ...sx,
          color: `#${ColorHexCodes.White}`,
          border: `2px solid #${ColorHexCodes.GridGray}`,
          borderRadius: 2,
          backgroundColor: `#${ColorHexCodes.GridGray}`,
        };
        break;
      case "text":
        sx = {
          ...sx,
          color: `#${ColorHexCodes.Main}`,
          backgroundColor: "transparent",
        };
    }
  } else {
    switch (buttonStyle) {
      case "primary":
        sx = {
          ...sx,
          color: `#${ColorHexCodes.White}`,
          border: `2px solid #${ColorHexCodes.Main}`,
          borderRadius: 2,
          backgroundColor: `#${ColorHexCodes.Main}`,
          "&:hover": {
            border: `2px solid #${ColorHexCodes.SubHeader}`,
            backgroundColor: `#${ColorHexCodes.Main}`,
          },
        };
        break;

      case "secondary":
        sx = {
          ...sx,
          color: `#${ColorHexCodes.Main}`,
          border: `2px solid #${ColorHexCodes.Main}`,
          borderRadius: 2,
          backgroundColor: `${ColorHexCodes.White}`,
          "&:hover": {
            color: `#${ColorHexCodes.SubHeader}`,
            border: `2px solid #${ColorHexCodes.SubHeader}`,
            backgroundColor: `${ColorHexCodes.White}`,
          },
        };
        break;

      case "negative":
        sx = {
          ...sx,
          color: `#${ColorHexCodes.White}`,
          border: `2px solid #${ColorHexCodes.GeneralRed}`,
          borderRadius: 2,
          backgroundColor: `#${ColorHexCodes.GeneralRed}`,
          "&:hover": {
            border: `2px solid #${ColorHexCodes.GeneralDarkRed}`,
            backgroundColor: `#${ColorHexCodes.GeneralRed}`,
          },
        };
        break;

      case "text":
        sx = {
          color: `#${ColorHexCodes.Main}`,
          backgroundColor: "transparent",
          ...sx,
        };
        break;
        
      case "bordered":
        sx = {
          ...sx,
          color: `#${ColorHexCodes.White}`,
          border: `2px solid #${ColorHexCodes.White}`,
          borderRadius: 2,
          backgroundColor: `#${ColorHexCodes.Main}`,
          "&:hover": {
            border: `2px solid #${ColorHexCodes.Blue}`,
            backgroundColor: `#${ColorHexCodes.SubBlue}`,
          },
        };
        break;
    }
  }

  return (
    <Button ref={ref} sx={sx} onClick={() => onClick()} fullWidth={fullWidth} disabled={disabled} size={size} startIcon={startIcon}>
      {children}
    </Button>
  );
}
